/* eslint-disable max-lines */
import { useEffect, useState } from "react";
import { ChevronLeft, Loader2, X } from "lucide-react";

import { GetDesignProjectDocument } from "graphql-api/operations";

import { useArtworkForm } from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import {
  useDesignProjectSync,
  useUpdateWithDeliveryEstimates
} from "@design-lab/features/design-project";
import { useApolloClient, useLazyQuery } from "@design-lab/shared/graphql";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useIsSample } from "@design-lab/shared/lib/config";
import { Button, TextField, Modal, ModalBody } from "@design-lab/shared/ui";

import { getUserType } from "@design-lab/entities/user";
import { redirectTo } from "common/utils/browser";
import { useSaveProject, triggerSavedProjectsTooltip } from "../hooks/useSaveProject";

export type SaveProjectProps = {
  artboardPreview?: React.ReactNode;
};

export function SaveProject(props: SaveProjectProps) {
  const { artboardPreview } = props;
  const { isDesktop } = useResponsive();
  const { setIsSaveProjectVisible } = useSaveProject();
  const { form: artworkForm } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const isSample = useIsSample();
  const apolloClient = useApolloClient();
  const userType = getUserType();

  const {
    mutateAsync: updateProjectMutationAsync,
    isPending: isUpdateProjectLoading,
    error: updateProjectError
  } = useUpdateWithDeliveryEstimates();
  const [getNewDesignProject] = useLazyQuery(GetDesignProjectDocument, {
    fetchPolicy: "network-only"
  });
  const { graphqlProjectData, isRestoring } = useDesignProjectSync();

  const [designName, setDesignName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError("Email is required");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value) {
      validateEmail(value);
    } else {
      setEmailError("Email is required");
    }
  };

  const isEmailValid = email && !emailError;

  const onUpdateProject = async (saveForLater = false) => {
    const id = artworkForm.getValues("designProjectUuid");
    const activeCompositionId = compositionForm.getValues("currentCompositionId");

    if (!id || !activeCompositionId) return;

    await updateProjectMutationAsync({
      id,
      payload: {
        madeDesignProject: {
          activeCompositionId,
          name: designName,
          email,
          phoneNumber
        },
        saveForLater
      }
    });

    const projectId = graphqlProjectData?.id;
    if (projectId) {
      const cacheId = apolloClient.cache.identify({
        __typename: "MadeDesignProject",
        id: projectId
      });
      if (cacheId) {
        apolloClient.cache.modify({
          id: cacheId,
          fields: {
            name: () => designName,
            email: () => email,
            phoneNumber: () => phoneNumber ?? undefined
          }
        });
      }
    }
  };

  const handleSaveAndStartNewProject = async () => {
    await onUpdateProject();

    const { data } = await getNewDesignProject({
      variables: {
        sample: isSample,
        uuid: undefined,
        new: true
      }
    });

    const newProjectUuid = data?.madeDesignProject?.uuid;
    if (newProjectUuid) {
      redirectTo(`/design-lab/${newProjectUuid}`);
    }
  };

  const handleSaveForLater = async () => {
    await onUpdateProject(true);
    setIsSaveProjectVisible(false);
    triggerSavedProjectsTooltip();
  };

  useEffect(() => {
    if (graphqlProjectData) {
      const { name, phoneNumber: phoneValue, email: emailValue } = graphqlProjectData as any;
      setDesignName(name || "");
      setPhoneNumber(phoneValue || "");
      setEmail(emailValue || "");
    }
  }, [graphqlProjectData]);

  const content = () => (
    <div className="bg-background flex flex-col overflow-hidden rounded-t-lg lg:rounded-lg">
      <div className="flex items-center justify-between border-b p-2 pl-4 lg:pl-2">
        <div className="flex items-center">
          {isDesktop && (
            <Button
              data-testid="save-project-close-button"
              variant="icon"
              onPress={() => setIsSaveProjectVisible(false)}
            >
              <ChevronLeft className="size-4" />
            </Button>
          )}
          <div>
            <h2 className="font-semibold">Save Project</h2>
            <p className="text-sm text-gray-500">
              Save your project so you can return and continue designing later.
            </p>
          </div>
        </div>
        <Button
          data-testid="save-project-close-button"
          variant="icon"
          onPress={() => setIsSaveProjectVisible(false)}
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 lg:p-10">
        <div className="max-w-105 mx-auto space-y-8 lg:space-y-4">
          {updateProjectError?.message && (
            <div className="text-sm font-semibold text-red-500">{updateProjectError.message}</div>
          )}

          <TextField
            label="Design Name"
            data-testid="design-name-input"
            value={designName}
            placeholder="Design Name"
            onChange={setDesignName}
          />
          <TextField
            label="Email"
            data-testid="email-input"
            value={email}
            placeholder="Email"
            onChange={handleEmailChange}
            isRequired
            isDisabled={userType === "signed_in" && !!email}
            {...(emailError && { errorMessage: emailError })}
          />
          <TextField
            label="Phone Number"
            data-testid="phone-number-input"
            type="number"
            value={phoneNumber || ""}
            placeholder="Phone Number"
            onChange={setPhoneNumber}
          />

          <div className="flex items-center">
            <Button
              variant="link"
              className="flex-1 text-blue-600"
              isDisabled={!isEmailValid || isUpdateProjectLoading}
              onPress={handleSaveAndStartNewProject}
            >
              Save & Start a New Project
            </Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              isDisabled={!isEmailValid || isUpdateProjectLoading}
              onPress={handleSaveForLater}
              isPending={isUpdateProjectLoading}
            >
              {isUpdateProjectLoading ? <Loader2 className="animate-spin" /> : "Save for Later"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isRestoring) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="size-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (isDesktop) {
    return (
      <div className="flex flex-1 gap-6 overflow-hidden p-6">
        {artboardPreview}
        {content()}
      </div>
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      className="bottom-0 top-[unset] flex h-fit max-h-[90vh] w-full translate-y-0 flex-col rounded-b-none"
      title="Save project"
      description=" Save your project so you can return and continue designing later."
    >
      <ModalBody className="flex overflow-hidden p-0">{content()}</ModalBody>
    </Modal>
  );
}
